import { Button, Card, CardBody, Input, Textarea } from '@foundation/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './sequences.new.module.css'
import { api, type CreateStepRequest } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/sequences/new')({
  component: NewSequencePage,
})

interface EmailStepDraft {
  delayDays: number
  subjectTemplate: string
  bodyTemplate: string
  ctaText: string
  ctaUrl: string
  discountOffer: string
}

const TRIGGER_OPTIONS = [
  {
    value: 'cancellation',
    icon: '🚫',
    label: 'Subscription Cancelled',
    description: 'Triggered when a customer cancels their subscription',
  },
  {
    value: 'trial_end',
    icon: '⏰',
    label: 'Trial Ended',
    description: 'Triggered when a free trial expires without converting',
  },
  {
    value: 'payment_failed',
    icon: '💳',
    label: 'Payment Failed',
    description: 'Triggered after repeated payment failures lead to churn',
  },
]

function createEmptyStep(stepNumber: number): EmailStepDraft {
  const defaultDelays: Record<number, number> = { 1: 0, 2: 3, 3: 7 }
  return {
    delayDays: defaultDelays[stepNumber] ?? (stepNumber - 1) * 3,
    subjectTemplate: '',
    bodyTemplate: '',
    ctaText: '',
    ctaUrl: '',
    discountOffer: '',
  }
}

function NewSequencePage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Step 1: Basics
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [trigger, setTrigger] = useState('cancellation')

  // Step 2: Email steps
  const [emailSteps, setEmailSteps] = useState<EmailStepDraft[]>([createEmptyStep(1)])

  const stepLabels = ['Basics', 'Email Steps', 'Review']

  const updateEmailStep = (index: number, updates: Partial<EmailStepDraft>) => {
    setEmailSteps((prev) => prev.map((step, i) => (i === index ? { ...step, ...updates } : step)))
  }

  const addEmailStep = () => {
    setEmailSteps((prev) => [...prev, createEmptyStep(prev.length + 1)])
  }

  const removeEmailStep = (index: number) => {
    if (emailSteps.length <= 1) return
    setEmailSteps((prev) => prev.filter((_, i) => i !== index))
  }

  const canProceedStep0 = name.trim().length > 0
  const canProceedStep1 =
    emailSteps.length > 0 &&
    emailSteps.every((s) => s.subjectTemplate.trim().length > 0 && s.bodyTemplate.trim().length > 0)

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      // Create the sequence
      const createReq: { name: string; description?: string } = {
        name: name.trim(),
      }
      if (description.trim()) {
        createReq.description = description.trim()
      }
      const sequenceResponse = await api.createSequence(createReq)

      const sequenceId = sequenceResponse.data.id

      // Create each step
      for (let i = 0; i < emailSteps.length; i++) {
        const step = emailSteps[i]
        if (!step) continue
        const stepData: CreateStepRequest = {
          step_number: i + 1,
          delay_days: step.delayDays,
          subject_template: step.subjectTemplate,
          body_template: step.bodyTemplate,
        }
        if (step.ctaText) stepData.cta_text = step.ctaText
        if (step.ctaUrl) stepData.cta_url = step.ctaUrl
        if (step.discountOffer) stepData.discount_offer = step.discountOffer
        await api.createStep(sequenceId, stepData)
      }

      toast.success('Sequence created successfully')
      void navigate({ to: '/sequences/$id', params: { id: sequenceId } })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create sequence')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getTriggerLabel = (value: string) => {
    return TRIGGER_OPTIONS.find((t) => t.value === value)?.label ?? value
  }

  return (
    <>
      {/* Stepper */}
      <div className={styles.stepper}>
        {stepLabels.map((label, index) => (
          <div
            key={label}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: index < stepLabels.length - 1 ? 1 : undefined,
            }}
          >
            <div
              className={`${styles.step} ${
                index === currentStep
                  ? styles.stepActive
                  : index < currentStep
                    ? styles.stepCompleted
                    : ''
              }`}
            >
              <span className={styles.stepNumber}>{index < currentStep ? '✓' : index + 1}</span>
              <span className={styles.stepLabel}>{label}</span>
            </div>
            {index < stepLabels.length - 1 && (
              <div
                className={`${styles.stepConnector} ${
                  index < currentStep ? styles.stepConnectorCompleted : ''
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Basics */}
      {currentStep === 0 && (
        <Card className={styles.formCard}>
          <CardBody>
            <h2 className={styles.sectionTitle}>Sequence Basics</h2>
            <p className={styles.sectionDescription}>
              Give your win-back sequence a name and configure when it should trigger.
            </p>

            <div className={styles.form}>
              <Input
                label="Sequence Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Win-Back, Premium Recovery"
                required
              />

              <Textarea
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this sequence..."
                rows={3}
              />

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    marginBottom: '0.5rem',
                  }}
                >
                  Trigger Event
                </label>
                <div className={styles.triggerOptions}>
                  {TRIGGER_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.triggerOption} ${
                        trigger === option.value ? styles.triggerOptionSelected : ''
                      }`}
                      onClick={() => setTrigger(option.value)}
                    >
                      <span className={styles.triggerOptionIcon}>{option.icon}</span>
                      <span className={styles.triggerOptionLabel}>{option.label}</span>
                      <span className={styles.triggerOptionDescription}>{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Step 2: Email Steps */}
      {currentStep === 1 && (
        <Card className={styles.formCard}>
          <CardBody>
            <h2 className={styles.sectionTitle}>Email Steps</h2>
            <p className={styles.sectionDescription}>
              Define the emails in your win-back sequence. Each step is sent after a configurable
              delay from the trigger event.
            </p>

            <div className={styles.stepsList}>
              {emailSteps.map((emailStep, index) => (
                <div key={index} className={styles.emailStep}>
                  <div className={styles.emailStepHeader}>
                    <span className={styles.emailStepTitle}>
                      <span className={styles.emailStepNumber}>{index + 1}</span>
                      Email Step {index + 1}
                    </span>
                    {emailSteps.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeEmailStep(index)}>
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className={styles.emailStepFields}>
                    <Input
                      label="Delay (days after trigger)"
                      type="number"
                      value={emailStep.delayDays.toString()}
                      onChange={(e) =>
                        updateEmailStep(index, {
                          delayDays: parseInt(e.target.value, 10) || 0,
                        })
                      }
                      min="0"
                    />

                    <Input
                      label="Subject Line"
                      type="text"
                      value={emailStep.subjectTemplate}
                      onChange={(e) =>
                        updateEmailStep(index, {
                          subjectTemplate: e.target.value,
                        })
                      }
                      placeholder="e.g., We miss you, {{customer_name}}!"
                      required
                    />

                    <Textarea
                      label="Email Body"
                      value={emailStep.bodyTemplate}
                      onChange={(e) =>
                        updateEmailStep(index, {
                          bodyTemplate: e.target.value,
                        })
                      }
                      placeholder="Write your win-back email content. Use {{customer_name}}, {{product_name}}, {{discount_code}} as template variables."
                      rows={5}
                      required
                    />

                    <div className={styles.fieldRow}>
                      <Input
                        label="CTA Button Text (optional)"
                        type="text"
                        value={emailStep.ctaText}
                        onChange={(e) => updateEmailStep(index, { ctaText: e.target.value })}
                        placeholder="e.g., Reactivate My Account"
                      />

                      <Input
                        label="CTA Button URL (optional)"
                        type="url"
                        value={emailStep.ctaUrl}
                        onChange={(e) => updateEmailStep(index, { ctaUrl: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>

                    <Input
                      label="Discount Offer (optional)"
                      type="text"
                      value={emailStep.discountOffer}
                      onChange={(e) =>
                        updateEmailStep(index, {
                          discountOffer: e.target.value,
                        })
                      }
                      placeholder="e.g., 20% off for 3 months"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.addStepButton}>
              <Button variant="secondary" onClick={addEmailStep}>
                + Add Email Step
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Step 3: Review */}
      {currentStep === 2 && (
        <Card className={styles.formCard}>
          <CardBody>
            <h2 className={styles.sectionTitle}>Review Your Sequence</h2>
            <p className={styles.sectionDescription}>
              Review your sequence configuration before creating it.
            </p>

            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>Sequence Name</div>
              <div className={styles.reviewValue}>{name}</div>

              {description && (
                <>
                  <div className={styles.reviewLabel}>Description</div>
                  <div className={styles.reviewValue}>{description}</div>
                </>
              )}

              <div className={styles.reviewLabel}>Trigger</div>
              <div className={styles.reviewValue}>{getTriggerLabel(trigger)}</div>

              <div className={styles.reviewLabel}>Email Steps ({emailSteps.length})</div>
              {emailSteps.map((step, index) => (
                <div key={index} className={styles.reviewStepCard}>
                  <div className={styles.reviewStepTitle}>
                    Step {index + 1}: {step.subjectTemplate || '(No subject)'}
                  </div>
                  <div className={styles.reviewStepMeta}>
                    Sent{' '}
                    {step.delayDays === 0
                      ? 'immediately'
                      : `${step.delayDays} day${step.delayDays !== 1 ? 's' : ''} after trigger`}
                    {step.ctaText && ` | CTA: ${step.ctaText}`}
                    {step.discountOffer && ` | Offer: ${step.discountOffer}`}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Navigation */}
      <div className={styles.navigation}>
        <div>
          {currentStep > 0 && (
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
          )}
        </div>
        <div className={styles.navigationRight}>
          {currentStep < 2 ? (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={
                (currentStep === 0 && !canProceedStep0) || (currentStep === 1 && !canProceedStep1)
              }
            >
              Next
            </Button>
          ) : (
            <Button variant="primary" onClick={() => void handleSubmit()} loading={isSubmitting}>
              Create Sequence
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
